import { Tag, TagColor } from "@aurem/ui";
import { FileUp } from "lucide-react";
import {
  gql,
  InternalDataImportJobOrderByColumn,
  InternalDataImportJobsFilterField,
} from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

const renderStatus = (value: unknown) => {
  const status = String(value);
  const colorMap: Record<string, TagColor> = {
    Completed: "success",
    Processing: "primary",
    Failed: "danger",
  };
  return <Tag color={colorMap[status] ?? "primary"} label={status} />;
};

const renderProgress = (value: unknown, item: unknown) => {
  const job = item as { processedRows?: number; totalRows?: number };
  const processed = job.processedRows ?? 0;
  const total = job.totalRows ?? 0;
  if (total === 0) return "-";
  const percentage = Math.round((processed / total) * 100);
  return `${processed}/${total} (${percentage}%)`;
};

const GET_DATA_IMPORT_JOB_LIST = gql(/* GraphQL */ `
  query InternalDataImportJobList(
    $pagination: Pagination
    $filters: InternalDataImportJobsFiltersExpression
    $orderBy: InternalDataImportJobOrderByInput
  ) {
    internalDataImportJobList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        uploadId
        handlerKey
        status
        totalRows
        processedRows
        lastError
        finishedAt
        createdAt
        updatedAt
      }
    }
  }
`);

export const dataImportJobResourceConfig: InternalResourceConfig = {
  resourceName: "data-import-job",
  displayNameKey: "apps.admin.internalResources.dataImportJob.title",
  basePath: "/internal/data/data-import-job",

  listQuery: GET_DATA_IMPORT_JOB_LIST,
  listQueryName: "internalDataImportJobList",

  fields: [
    {
      key: "status",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.status",
      type: FieldType.String,
      format: renderStatus,
      sortKey: InternalDataImportJobOrderByColumn.Status,
      width: 120,
    },
    {
      key: "handlerKey",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.handlerKey",
      type: FieldType.String,
      sortKey: InternalDataImportJobOrderByColumn.HandlerKey,
      width: 200,
    },
    {
      key: "uploadId",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.uploadId",
      type: FieldType.Uuid,
      width: 150,
    },
    {
      key: "processedRows",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.progress",
      type: FieldType.String,
      format: renderProgress,
      sortKey: InternalDataImportJobOrderByColumn.TotalRows,
      width: 130,
    },
    {
      key: "lastError",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.lastError",
      type: FieldType.String,
      width: { min: 100, grow: 1 },
    },
    {
      key: "finishedAt",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.finishedAt",
      type: FieldType.DateTime,
      width: 160,
    },
    {
      key: "createdAt",
      labelKey: "apps.admin.internalResources.dataImportJob.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalDataImportJobOrderByColumn.CreatedAt,
      width: 160,
    },
  ],

  list: {
    defaultSort: {
      column: InternalDataImportJobOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: [
      InternalDataImportJobsFilterField.HandlerKey,
      InternalDataImportJobsFilterField.Status,
      InternalDataImportJobsFilterField.UploadId,
    ],
  },

  navigation: {
    icon: <FileUp size={16} />,
    order: 6,
  },
};
